import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, Plus, Trash2, GripVertical, Link } from 'lucide-react';
import { uploadWebsiteBlockImage } from '../../../../services/storageService';
import { generateCardId } from './schema';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';
import LinkInput from '../common/LinkInput';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getCardGridExtraStyleControls = ({ content, onChange }) => ({
  sizeSpacingExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Card Text
      </h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Alignment</label>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ cardTextAlign: value })}
              className={`py-1.5 rounded text-sm font-medium transition-colors ${
                (content.cardTextAlign || 'left') === value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  ),
});

/**
 * CardGridEditor - Editor component for card-grid block
 */
const CardGridEditor = ({ block, onChange, profileId, theme, hideStylePanels, pages }) => {
  const { content } = block;
  const [uploadingCardId, setUploadingCardId] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRefs = useRef({});

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleCardChange = (cardId, field, value) => {
    const updatedItems = content.items.map((item) =>
      item.id === cardId ? { ...item, [field]: value } : item
    );
    handleChange('items', updatedItems);
  };

  const handleAddCard = () => {
    const newCard = {
      id: generateCardId(),
      title: 'Card Title',
      description: 'Add a description for this card.',
      image: '',
      ctaText: '',
      ctaLink: '',
    };
    handleChange('items', [...content.items, newCard]);
  };

  const handleRemoveCard = (cardId) => {
    handleChange(
      'items',
      content.items.filter((item) => item.id !== cardId)
    );
  };

  const handleImageUpload = async (cardId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCardId(cardId);
    setUploadError('');

    try {
      const downloadURL = await uploadWebsiteBlockImage(file, profileId, `${block.id}_${cardId}`);
      handleCardChange(cardId, 'image', downloadURL);
    } catch (error) {
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setUploadingCardId(null);
      if (fileInputRefs.current[cardId]) {
        fileInputRefs.current[cardId].value = '';
      }
    }
  };

  const handleRemoveCardImage = (cardId) => {
    handleCardChange(cardId, 'image', '');
  };

  return (
    <div className="space-y-4">
      {/* Section Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Featured Items"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Columns */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Columns
        </label>
        <div className="flex gap-2">
          {[2, 3, 4].map((cols) => (
            <button
              key={cols}
              onClick={() => handleChange('columns', cols)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.columns === cols
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {cols} Columns
            </button>
          ))}
        </div>
      </div>

      {/* Show Images Toggle */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-300">
          Show Card Images
        </label>
        <button
          onClick={() => handleChange('showImages', !content.showImages)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            content.showImages ? 'bg-[#749555]' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              content.showImages ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Cards */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Cards ({content.items?.length || 0})
        </label>

        <div className="space-y-3">
          {content.items?.map((card, index) => (
            <div
              key={card.id}
              className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-gray-400">
                  <GripVertical size={16} />
                  <span className="text-sm">Card {index + 1}</span>
                </div>
                <button
                  onClick={() => handleRemoveCard(card.id)}
                  className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                  disabled={content.items.length <= 1}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Card Image */}
              {content.showImages && (
                <div className="mb-3">
                  <input
                    ref={(el) => (fileInputRefs.current[card.id] = el)}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(card.id, e)}
                    className="hidden"
                  />

                  {card.image ? (
                    <div className="relative rounded-lg overflow-hidden">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-24 object-cover"
                      />
                      <button
                        onClick={() => handleRemoveCardImage(card.id)}
                        className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRefs.current[card.id]?.click()}
                      disabled={uploadingCardId === card.id}
                      className="w-full h-20 border border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-[#749555] hover:text-[#749555] transition-colors disabled:opacity-50 text-sm"
                    >
                      {uploadingCardId === card.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <>
                          <Upload size={18} />
                          <span className="mt-1">Add Image</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Card Title */}
              <input
                type="text"
                value={card.title || ''}
                onChange={(e) => handleCardChange(card.id, 'title', e.target.value)}
                placeholder="Card Title"
                className="w-full px-3 py-2 mb-2 bg-[#0d0d1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm"
              />

              {/* Card Description */}
              <textarea
                value={card.description || ''}
                onChange={(e) => handleCardChange(card.id, 'description', e.target.value)}
                placeholder="Card description..."
                rows={2}
                className="w-full px-3 py-2 mb-2 bg-[#0d0d1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none text-sm"
              />

              {/* CTA Button */}
              <div className="pt-2 border-t border-gray-700">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-2">
                  <Link size={12} />
                  Call to Action (Optional)
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={card.ctaText || ''}
                    onChange={(e) => handleCardChange(card.id, 'ctaText', e.target.value)}
                    placeholder="Button text..."
                    className="w-full px-3 py-2 bg-[#0d0d1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm"
                  />
                  <LinkInput
                    value={card.ctaLink || ''}
                    onChange={(val) => handleCardChange(card.id, 'ctaLink', val)}
                    pages={pages}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {uploadError && (
          <p className="text-red-400 text-sm mt-2">{uploadError}</p>
        )}

        {/* Add Card Button */}
        <button
          onClick={handleAddCard}
          className="mt-3 w-full py-2 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-[#749555] hover:text-[#749555] transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          Add Card
        </button>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['card-grid']}
            extraControls={getCardGridExtraStyleControls({ content, onChange }).sizeSpacingExtras}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['card-grid']}
            theme={theme || {}}
            blockType="card-grid"
          />
        </>
      )}
    </div>
  );
};

export default CardGridEditor;
